import { memo, useState } from 'react';
import { SiteElement, SkiPlan } from '../types';
import { Flag, FlagTriangleRight, Coffee, Paintbrush, Clock, Repeat, Cross, Users, MapPin, Plus, Type, Video, Target, ParkingCircle, DoorOpen, Dumbbell, Home, AlertTriangle, Warehouse, Building2, Mic, RotateCw, User, TrendingUp, Mountain, MoreVertical } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { IconPicker } from './IconPicker';
import { ELEMENT_TOOLS, ELEMENT_COLORS, DEFAULT_ELEMENT_ICONS, WCIcon } from '../utils/elementConfig';

interface ElementToolbarProps {
  activeType: SiteElement['type'] | null;
  onSelectType: (type: SiteElement['type'] | null) => void;
  customIcons?: Record<SiteElement['type'], string>;
  onCustomIconChange?: (type: SiteElement['type'], iconName: string) => void;
}

export const ElementToolbar = memo(function ElementToolbar({ 
  activeType, 
  onSelectType,
  customIcons = {},
  onCustomIconChange
}: ElementToolbarProps) {
  const [iconPickerOpen, setIconPickerOpen] = useState<SiteElement['type'] | null>(null);
  
  const getIcon = (type: SiteElement['type']) => {
    if (customIcons[type]) {
      const CustomIcon = (LucideIcons as any)[customIcons[type]];
      if (CustomIcon) return CustomIcon;
    }
    return DEFAULT_ELEMENT_ICONS[type];
  };

  const handleIconChange = (type: SiteElement['type'], iconName: string) => {
    if (onCustomIconChange) {
      onCustomIconChange(type, iconName);
    }
  };

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-2">
      <p className="text-xs text-gray-600 mb-2 text-center">Sélectionnez un élément puis cliquez sur l'image pour le placer</p>
      <div className="flex justify-center">
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-1">
          {ELEMENT_TOOLS.map((tool) => {
            const Icon = getIcon(tool.type);
            const isActive = activeType === tool.type;
            const color = ELEMENT_COLORS[tool.type];
            return (
              <div key={tool.type} className="relative group">
                <button
                  type="button"
                  onClick={() => onSelectType(isActive ? null : tool.type)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all w-full ${
                    isActive
                      ? 'border-blue-600 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: color }}
                  >
                    <Icon className={tool.type === 'wc' ? '' : 'w-4 h-4 text-white'} />
                  </div>
                  <span className="text-[10px] text-center leading-tight">{tool.label}</span>
                </button>
                
                {/* Icon customization button */}
                {onCustomIconChange && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIconPickerOpen(tool.type);
                    }}
                    className="absolute top-0 right-0 w-4 h-4 bg-gray-700 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-900 z-10"
                    title="Personnaliser l'icône"
                  >
                    <MoreVertical className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Icon Picker Modal */}
      {iconPickerOpen && (
        <IconPicker
          currentIcon={customIcons[iconPickerOpen]}
          onSelect={(iconName) => handleIconChange(iconPickerOpen, iconName)}
          onClose={() => setIconPickerOpen(null)}
        />
      )}
    </div>
  );
});